import { Loader2, Plus } from 'lucide-react';

import { AdsWaitState } from '@/components/google-ads/AdsWaitState';
import { CollapsibleCard } from '@/components/google-ads/CollapsibleCard';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
// CORE-W-04: Input / Dialog / Label / Textarea moved with the Annotate
// flow into ConversionActionsPanel — they were the only remaining
// consumers here.
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
// CORE-M-10.a: AdHealthTable import removed with the Ad Health block. The
// Ad Review tab (CORE-M-06) imports it directly.
// CORE-M-10.d: LocationPicker + GeoTargetSuggestion imports removed with
// the Locations block. Editing lives on Campaign Settings now.
import { StrategyBoard } from '@/components/google-ads/StrategyBoard';
import { budgetHeadline, fmtConv, fmtMoney } from '@/features/ads/management/managementFormat';
import { ReadGate } from '@/features/ads/management/ReadGate';
import {
  MANAGEMENT_WINDOWS,
  useManagementWindow,
} from '@/features/ads/management/useManagementWindow';
// CORE-M-10.b: ConversionActionsPanel import removed with the Conversion
// Actions block. The panel lives on WhatConverts (CORE-W-04) now.
import {
  ManagementTable,
  type ManagementColumn,
} from '@/components/google-ads/ManagementTable';
import {
  getAdMonitor,
  getChangeHistory,
  getConversionView,
  // CORE-M-10.g: getPmaxChannels removed with the PMax block. The
  // Performance Max tab imports it directly.
  getStructure,
  // CORE-M-10.c: setAdGroupStatus removed with the Ad Groups block.
  // Ad-group pause/enable is a regression pending a drill-down write
  // path (follow-up CORE-M-10.c.b).
  // CORE-M-10.d: addLocations / removeLocations removed with the
  // Locations block. Editing lives on the Campaign Settings tab now.
  // CORE-M-10.e: addAdScheduleSlots / removeAdScheduleSlots removed
  // with the Ad Schedule block. Editing lives on Campaign Settings now.
  // CORE-W-04: updateConversionActionOverride moved into
  // ConversionActionsPanel with the Annotate dialog.
  type ChangeHistoryResponse,
  type ChangeRow,
  // CORE-W-04: ConversionActionRow type moved with the Annotate dialog.
  // CORE-M-10.g: PmaxChannelResponse type removed with the PMax block.
  // CORE-M-10.c: AdGroupRow type removed with the Ad Groups block.
  // The Campaign drill-down imports its own row type.
  // CORE-M-10.f: ExtensionRow import removed with the Extensions block.
  // The Campaign Settings tab imports it directly.
  // CORE-M-10.g: PmaxChannelRow type removed with the PMax block.
  type StructureResponse,
  type TargetingRow,
  type ReadState,
  // CORE-M-10.d + CORE-M-10.e: BlastRadiusConfirmationDetail and
  // LocationWriteResponse imports removed with the Locations + Ad Schedule
  // writeback flows.
} from '@/services/googleAdsManagementService';

/** ⛔ WHAT THE ROW COUNT ACTUALLY COUNTS.
 *  Locations and ad schedules come from `campaign_criterion`, which returns ONE
 *  ROW PER CAMPAIGN × CRITERION — not one per place. A local plumber with 10
 *  campaigns each targeting the same 175 postcodes produces 1,750 rows, and the
 *  table dutifully says so. That number is arithmetically right and reads as
 *  "this account's targeting is broken", which is the sort of thing that makes
 *  an operator stop trusting the screen.
 *
 *  I put "1,750 location targets — that looks high to me" in the operator test
 *  doc and asked Art to check it against Google Ads. The tool should have said
 *  what it meant instead of asking a human to reverse-engineer it.
 */
export function pairSummary(
  rows: TargetingRow[] | undefined,
  noun: string,
): string | undefined {
  if (!rows || rows.length === 0) return undefined;
  const campaigns = new Set(rows.map((r) => r.campaign_name)).size;
  const targeted = new Set(rows.filter((r) => !r.is_negative).map((r) => r.label)).size;
  const excluded = new Set(rows.filter((r) => r.is_negative).map((r) => r.label)).size;
  const parts = [
    `${targeted.toLocaleString()} ${noun}${targeted === 1 ? '' : 's'}`,
    `across ${campaigns} campaign${campaigns === 1 ? '' : 's'}`,
  ];
  // Excluded places are a different thing entirely; folding them into one
  // number is how "targeted" and "blocked" become indistinguishable.
  if (excluded > 0) parts.push(`· ${excluded.toLocaleString()} excluded`);
  return parts.join(' ');
}

/** One campaign choice for the Add location / Add time slot forms — derived
 *  from whichever LOCATION/AD_SCHEDULE rows the account already has, since
 *  neither structure endpoint carries a plain list of every campaign's
 *  resource_name. In practice this covers every Search/PMax campaign: Google
 *  Ads requires at least one positive geo target to serve at all. A campaign
 *  with literally zero LOCATION and zero AD_SCHEDULE rows (only reachable
 *  right after a bug, not through normal account setup) would not appear
 *  here — a known gap, not an oversight. */
function campaignOptionsFrom(structure: StructureResponse | undefined): {
  resourceName: string;
  name: string;
}[] {
  if (!structure) return [];
  const byResourceName = new Map<string, string>();
  for (const row of [...structure.locations, ...structure.ad_schedules]) {
    if (row.campaign_resource_name && !byResourceName.has(row.campaign_resource_name)) {
      byResourceName.set(row.campaign_resource_name, row.campaign_name);
    }
  }
  return [...byResourceName.entries()]
    .map(([resourceName, name]) => ({ resourceName, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

// CORE-M-10.d + CORE-M-10.e: parseTargetingBlastRadiusDetail,
// targetingErrorDetail and BlastRadiusRemoveDialog were the shared helpers
// for the deleted Locations + Ad Schedule writeback flows. The Campaign
// Settings tab (CORE-S-02) has its own inlined copy tuned to its dialog
// component, so these are now orphaned and removed.

// CORE-M-10.d: AddLocationForm removed with the Locations block. Editing
// lives on the Campaign Settings tab now.

// CORE-M-10.e: AddAdScheduleForm (+ its DAYS_OF_WEEK / HOURS constants)
// removed with the Ad Schedule block. Editing lives on the Campaign
// Settings tab now.

// Gap A / CORE-M-12: the window list moved to useManagementWindow so the
// picker here and every consumer that used to hardcode 30 read one source.

/**
 * Google's own ceiling on change history, mirroring MAX_WINDOW_DAYS in
 * backend/api/services/google_ads/change_history_service.py. The route
 * rejects anything larger by design, so the one card that cannot honour a
 * 90-day selection clamps to this and says why (CORE-M-12).
 */
const CHANGE_HISTORY_MAX_DAYS = 30;

/**
 * Management surfaces an operator would otherwise open Google Ads for
 * (ops-coverage #4, #5, #7). View only.
 *
 * ⛔ EVERY SECTION BRANCHES ON read_ok BEFORE length.
 * The API returns `read_ok: false` with an empty list when it could not reach
 * Google. Rendering that as "nothing to report" is the failure that told a
 * client they had zero spend in a month they spent $3,953 — the screen showed a
 * result where it should have shown an absence. ReadGate below exists so no
 * section can forget.
 */
export function AdsManagementTab({ tenantId }: { tenantId: string }) {
  // ⛔ ONE WINDOW FOR THE WHOLE SCREEN. Before this, every card and table was
  // hardcoded to its own 30 days. Two panels drawn over different ranges is
  // two instruments that quietly disagree, and the operator cannot tell which
  // one lied — Viktor: "one global picker, printed on the page".
  //
  // ⚠ That claim had stopped being true of the PAGE. This state was local to
  // this component, so it reached these four queries and nothing else: the Ad
  // Review tab, the category-bar counts and the campaign drill-down each kept
  // their own hardcoded 30, which is why selecting 90 days left Ad Review
  // printing "last 30 days" (Art, CORE-M-12). It now lives in the URL, so one
  // picker really does drive the screen.
  const [days, setDays] = useManagementWindow();
  // CORE-M-10.a: `focus` state removed with the Ad Health block. The
  // SummaryTiles onFocus callback is now a no-op — the tiles still
  // count what they counted before, but there is no in-page section
  // to scroll to since Ad Health lives on the Ad Review tab now.

  // CORE-M-12: the change-history endpoint declares `days` with
  // `le=MAX_WINDOW_DAYS` (30) and rejects anything larger ON PURPOSE —
  // "rejected rather than silently truncated" per its own description.
  // Passing the page selector straight through therefore 422'd the request
  // at 90 days, so the card printed "Could not read this from Google Ads"
  // (a read failure) when the truth is that Google only keeps 30 days.
  // Clamp here and say so in the description below, rather than loosening a
  // deliberate backend contract.
  const changeHistoryDays = Math.min(days, CHANGE_HISTORY_MAX_DAYS);
  const changes = useQuery({
    queryKey: ['ga-change-history', tenantId, changeHistoryDays],
    queryFn: () => getChangeHistory(tenantId, changeHistoryDays),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });
  const conversions = useQuery({
    queryKey: ['ga-conversion-view', tenantId, days],
    queryFn: () => getConversionView(tenantId, days),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });
  const structure = useQuery({
    queryKey: ['ga-structure', tenantId, days],
    queryFn: () => getStructure(tenantId, days),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });
  // Gap A phase 2: the keyword pause/enable mutation moved with the
  // Keywords table to KeywordReviewTab.

  // CORE-M-10.c: adGroupStatus mutation removed with the Ad Groups
  // block. Inline pause/enable in the drill-down is follow-up
  // CORE-M-10.c.b.

  // --- location targeting + ad schedule ADD/REMOVE (Management Phase 3) ---
  //
  // ⚠ Non-null blastRadius switches the confirm dialog into typed-confirmation
  // mode, mirroring SearchTermsNegativesTab exactly. pendingRemoval holds the
  // resource_names the operator asked to remove — captured at click time,
  // independent of ManagementTable's own selection (which clears immediately
  // on click, before the mutation resolves).
  // CORE-M-10.d: location blast-radius + pending-removal state removed
  // with the Locations block. Editing lives on the Campaign Settings tab
  // now.

  // CORE-M-10.e: schedule blast-radius + pending-removal state and the
  // addScheduleMutation / removeScheduleMutation hooks removed with the
  // Ad Schedule block. Editing lives on the Campaign Settings tab now.

  // --- Conversion action annotations (Management #5 Phase 4) ---------------
  // ⚠ NOT a Google Ads write — a plain operator annotation stored against our
  // own Postgres. No GOOGLE_ADS_ENABLED gate on the backend, no blast radius.
  // CORE-W-04: annotate state / mutation moved into ConversionActionsPanel.

  // CORE-M-10.g: `pmax` query removed with the PMax block. The
  // Performance Max tab (CORE-P-01) reads the same endpoint itself.

  const ads = useQuery({
    queryKey: ['ga-ad-monitor', tenantId, days],
    queryFn: () => getAdMonitor(tenantId, days),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });

  // ⚠ PAGINATE, DO NOT TRUNCATE. Change history previously rendered
  // .slice(0, 40) with nothing saying so — an account with 200 changes showed 40
  // and looked complete. Silent truncation reads as "that is all there is".
  // Paging now lives inside ManagementTable, along with search, sorting,
  // resizing and filtering, so all three tables behave identically.
  // CORE-W-04: conversionColumns moved into ConversionActionsPanel.

  // CORE-M-10.c: adGroupColumns removed with the Ad Groups block. The
  // Campaign drill-down (M-11.b) renders per-campaign ad groups inline.


  const targetingColumns = useMemo<ManagementColumn<TargetingRow>[]>(
    () => [
      { key: 'label', header: 'Target', width: 240, sort: (r) => r.label,
        render: (r) => (
          <span className={r.is_negative ? 'text-destructive' : ''}>
            {r.is_negative ? `Excluded: ${r.label}` : r.label}
          </span>
        ) },
      { key: 'campaign', header: 'Campaign', width: 220,
        sort: (r) => r.campaign_name.toLowerCase(), render: (r) => r.campaign_name },
      { key: 'bid_modifier', header: 'Bid adj.', width: 100, align: 'right',
        sort: (r) => r.bid_modifier,
        render: (r) => r.bid_modifier
          ? `${(r.bid_modifier * 100).toFixed(0)}%`
          : <span className="text-muted-foreground">—</span> },
    ], []);

  // CORE-M-10.f: extensionColumns removed with the Extensions block.
  // Editing lives on the Campaign Settings tab (view-only per spec).

  // Campaign picker for the Add location / Add time slot forms — see
  // campaignOptionsFrom's docstring for the coverage caveat.
  const campaignOptions = useMemo(
    () => campaignOptionsFrom(structure.data),
    [structure.data],
  );

  // CORE-M-10.g: pmaxColumns removed with the PMax block. The Performance
  // Max tab (CORE-P-01) renders channels itself.

  const changeColumns = useMemo<ManagementColumn<ChangeRow>[]>(
    () => [
      {
        key: 'when',
        header: 'When',
        width: 170,
        sort: (c) => c.change_date_time,
        render: (c) => <span className="tabular-nums">{c.change_date_time}</span>,
      },
      {
        key: 'who',
        header: 'Who',
        width: 200,
        sort: (c) => (c.made_by_a_person ? c.user_email : 'Google (automated)'),
        render: (c) =>
          // ⛔ Never show an automated change as a person's edit. That sends an
          // operator asking a teammate why they changed something they never
          // touched.
          c.made_by_a_person ? (
            c.user_email
          ) : (
            <span className="text-muted-foreground">Google (automated)</span>
          ),
      },
      {
        key: 'what',
        header: 'What',
        width: 190,
        sort: (c) => c.resource_type,
        render: (c) => (
          <>
            {c.resource_type}
            {c.is_budget_change && (
              <Badge className="ml-2" variant="destructive">
                budget
              </Badge>
            )}
          </>
        ),
      },
      {
        key: 'change',
        header: 'Change',
        width: 300,
        sort: (c) => c.budget_delta ?? 0,
        render: (c) => (
          // ⛔ The movement, not the struct member Google touched.
          <span
            className={
              c.budget_delta != null && c.budget_delta !== 0
                ? 'font-medium'
                : 'text-muted-foreground'
            }
          >
            {c.summary || c.changed_fields.join(', ')}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-4">
      <StrategyBoard
        tenantId={tenantId}
        ads={ads.data}
        conversions={conversions.data}
        structure={structure.data}
        adsLoading={ads.isLoading}
        structureLoading={structure.isLoading}
        conversionsLoading={conversions.isLoading}
        adsError={ads.isError}
        structureError={structure.isError}
        conversionsError={conversions.isError}
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {/* ⚠ The window Google ACTUALLY answered over, read back from the
              response — not the number requested. A clamped range printed as
              the requested one is a label that lies. */}
          Every figure on this page covers the last{' '}
          <span className="font-medium text-foreground">
            {ads.data?.window_days ?? days} days
          </span>
          .
          {changes.data?.read_ok &&
          (changes.data.window_days ?? 0) < (ads.data?.window_days ?? days) ? (
            <>
              {' '}
              Change history is the exception: Google keeps only{' '}
              {changes.data.window_days} days of it and it cannot be backfilled.
            </>
          ) : null}
        </p>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-[170px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MANAGEMENT_WINDOWS.map((w) => (
              <SelectItem key={w.days} value={String(w.days)}>
                {w.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {/* Gap A phase 2c: the verdict banner, the write-actions statement and
          the Performance Max note moved to
          features/ads/management/ManagementAccountCaveats.tsx, which renders
          ABOVE the category bar. All three are true of the account whichever
          tab is open, so stating them inside a tab body repeated them under
          all seven tabs. */}

      {/* --- Google recommendations ------------------------------------ */}
      <CollapsibleCard
        id="google-recommendations"
        title="Google recommendations"
        description={
          <>
            Live recommendations from Google Ads. Read only. Open Google Ads
            to apply or dismiss. Core holds spend, bidding, and match-type
            types.
          </>
        }
      >
        <p className="text-sm text-muted-foreground">
          Google's recommendations are read-only here. Apply or dismiss in
          Google Ads.
        </p>
      </CollapsibleCard>

      {/* CORE-M-10.a: the Ad Health block moved out of Management body.
          Waste + dark ad groups now live on the Ad Review tab (CORE-M-06);
          per-campaign "below Good" ad-strength surfaces as a chip on the
          Campaign structure drill-down (CORE-M-10.a → CampaignRow). The
          AdHealthTable component is unchanged and is reused by the Ad
          Review tab; only this block on the ops page is deleted. */}

      {/* CORE-M-10.b: <CollapsibleCard id="conversion-actions"> removed.
          Its home is the WhatConverts tab (CORE-W-04). The verdict banner
          and summary tiles above still read from the conversions query,
          which stays. */}

      {/* --- Ad groups / Keywords / Locations / Ad schedule ---------------- */}
      {/* CORE-M-10.c: <CollapsibleCard id="ad-groups"> removed. The
          Campaign structure drill-down (M-11.b) renders per-campaign ad
          groups inline, ranked by spend. Bulk pause/enable is a
          regression here — filed as follow-up CORE-M-10.c.b. */}

      {/* Gap A phase 2: the Keywords card moved to the Keyword Review tab
          (features/ads/management/KeywordReviewTab.tsx). It was the largest
          block on this page — 779 rows on one account — and it rendered
          identically under all seven category tabs, which is most of why
          the page looked the same whichever tab you were on. */}

      {/* CORE-M-10.d: the Locations block moved to the Campaign Settings
          tab (read + Remove). The block was deleted here in this PR;
          Add-a-new-row still runs through S-02.b once it ships. */}

      {/* CORE-M-10.e: the Ad Schedule block moved to the Campaign Settings
          tab (read + Remove). The block was deleted here in this PR;
          Add-a-new-slot still runs through S-02.b once it ships. */}

      {/* CORE-M-10.f: the Extensions block moved to the Campaign Settings
          tab (view-only). The block was deleted here in this PR;
          Extensions stay view-only per spec §S-02. */}

      {/* CORE-M-10.e: schedule BlastRadiusRemoveDialog removed with the
          Ad Schedule block. Editing lives on the Campaign Settings tab. */}

      {/* CORE-M-10.g: the PMax block moved to the Performance Max tab
          (CORE-P-01), which also absorbed Placement Review (CORE-P-02).
          Where PMax spent + channels breakdown live there now. */}

      {/* --- Change history ---------------------------------------------- */}
      <CollapsibleCard
        id="change-history"
        title="Change history"
        description={
          <>
            {/* ⚠ The 30-day cap is stated, not implied. Day 31 rendering as "no
                changes" would read as "nothing happened" when it means "we
                cannot know". */}
            Google keeps {changes.data?.window_days ?? CHANGE_HISTORY_MAX_DAYS}{' '}
            days of change history and never logs dismissals.
            {days > CHANGE_HISTORY_MAX_DAYS ? (
              <>
                {' '}That is why this list stays at{' '}
                {changes.data?.window_days ?? CHANGE_HISTORY_MAX_DAYS} days while
                the rest of the page is at {days}.
              </>
            ) : null}
          </>
        }
      >
          <ReadGate state={changes.data} isLoading={changes.isLoading}>
            {changes.data && changes.data.budget_changes.length > 0 && (
              <div className="mb-3 rounded-md border border-coral/40 bg-coral-light/20 p-3 text-sm">
                {/* ⛔ A COUNT IS NOT AN AMOUNT. "27 budget changes" sounds like
                    churn and says nothing about the money; it was also mostly
                    ~3 batch events (five writes inside 30 seconds by one
                    account), so the count overstates human activity. The net
                    daily movement is the number worth reading. */}
                <p className="font-medium">{budgetHeadline(changes.data)}</p>
                <p className="text-muted-foreground">
                  Budget changes move money immediately.
                </p>
              </div>
            )}
            <ManagementTable<ChangeRow>
              rows={changes.data?.changes ?? []}
              columns={changeColumns}
              getRowId={(c) =>
                `${c.change_date_time}-${c.resource_type}-${c.changed_fields.join('|')}`
              }
              searchFields={(c) => [c.user_email, c.resource_type, ...c.changed_fields]}
              filters={[
                { key: 'what', label: 'What', value: (c) => c.resource_type },
                {
                  key: 'who',
                  label: 'Who',
                  value: (c) =>
                    c.made_by_a_person ? c.user_email : 'Google (automated)',
                },
              ]}
              defaultSortKey="when"
              defaultSortDir="desc"
              searchPlaceholder="Search changes…"
              emptyText={changes.data?.message ?? 'No changes recorded.'}
              rowTone={(c) => (c.is_budget_change ? 'warn' : '')}
            />
          </ReadGate>
      </CollapsibleCard>

    </div>
  );
}

export default AdsManagementTab;
